import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService, SortEvent } from 'primeng/api';
import { Subscription } from 'rxjs';
import { ExtratoEstoqueDTO } from '../../../models/estoque/extrato-estoque.model';
import { EstoqueService } from '../../../services/estoque/estoque.service';
import { TipoOperacaoDeEstoque } from '../../../enums/tipoOperacaoDeEstoque.enum';
import { TipoTransacao } from '../../../enums/TipoTransacao.enum';
import { TableColumn } from '../../../models/shared/table-column.model';

@Component({
  selector: 'app-extrato-estoque',
  templateUrl: './extrato-estoque.component.html',
  styleUrls: ['./extrato-estoque.component.scss'],
  providers: [MessageService],
})
export class ExtratoEstoqueComponent implements OnInit, OnDestroy {

  // Dados
  extratos: ExtratoEstoqueDTO[] = [];
  loading: boolean = true;
  totalRecords: number = 0;
  first: number = 0;
  rows: number = 20;

  // Info do item (extraido do primeiro registro)
  itemId: number = 0;
  itemNome: string = '';
  itemCodigo: number = 0;

  // Enums para uso no template
  TipoOperacaoDeEstoque = TipoOperacaoDeEstoque;
  TipoTransacao = TipoTransacao;

  // Busca
  searchTerm: string = '';

  // Ordenacao backend
  lastfield: string = 'id';
  lastSort: string = 'asc';

  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'extrato-estoque-visible-columns';

  columns: TableColumn[] = [
    { key: 'codigoEstoque', labelKey: 'extrato_estoque.labels.codigo_estoque', visible: true, fixed: true, defaultVisible: true },
    { key: 'tipoOperacao', labelKey: 'extrato_estoque.labels.tipo_operacao', visible: true, fixed: false, defaultVisible: true },
    { key: 'quantidade', labelKey: 'extrato_estoque.labels.quantidade', visible: true, fixed: false, defaultVisible: true },
    { key: 'saldoAnterior', labelKey: 'extrato_estoque.labels.saldo_anterior', visible: true, fixed: false, defaultVisible: true },
    { key: 'saldo', labelKey: 'extrato_estoque.labels.saldo_final', visible: true, fixed: false, defaultVisible: true },
    { key: 'origem', labelKey: 'extrato_estoque.labels.origem', visible: true, fixed: false, defaultVisible: true },
    { key: 'emissao', labelKey: 'extrato_estoque.labels.data_emissao', visible: true, fixed: false, defaultVisible: true },
  ];

  // Objetos de traducao (padrao moderno - tipo literal implicito)
  texts = {
    titulo: '',
    subtitulo: '',
    nenhuma_movimentacao: '',
    ajuste_filtros: '',
    carregando: '',
    erro_carregar: '',
  };

  labels = {
    tipo_operacao: '',
    data_emissao: '',
    codigo_estoque: '',
    quantidade: '',
    saldo_anterior: '',
    saldo_final: '',
    origem: '',
    entrada: '',
    saida: '',
    ajuste_de_estoque: '',
    nota_fiscal: '',
    pedido: '',
    itens_por_pagina: '',
    item: '',
    venda: '',
    compra: '',
    devolucao_de_venda: '',
    devolucao_de_compra: '',
  };

  placeholders = {
    pesquisar: '',
  };

  tooltips = {
    voltar: '',
    ver_ajuste: '',
    ver_nota: '',
  };

  botoes = {
    voltar: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private estoqueService: EstoqueService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadColumnPreferences();
    this.loadTranslations();

    this.route.params.subscribe((params) => {
      this.itemId = Number(params['itemId']);
      this.loadExtrato();
    });

    // Monitorar mudanca de idioma
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // =========================================================================
  // Traducoes
  // =========================================================================

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('extrato_estoque.texts.titulo'),
      subtitulo: this.translate.instant('extrato_estoque.texts.subtitulo'),
      nenhuma_movimentacao: this.translate.instant('extrato_estoque.texts.nenhuma_movimentacao'),
      ajuste_filtros: this.translate.instant('extrato_estoque.texts.ajuste_filtros'),
      carregando: this.translate.instant('extrato_estoque.texts.carregando'),
      erro_carregar: this.translate.instant('extrato_estoque.texts.erro_carregar'),
    };

    this.labels = {
      tipo_operacao: this.translate.instant('extrato_estoque.labels.tipo_operacao'),
      data_emissao: this.translate.instant('extrato_estoque.labels.data_emissao'),
      codigo_estoque: this.translate.instant('extrato_estoque.labels.codigo_estoque'),
      quantidade: this.translate.instant('extrato_estoque.labels.quantidade'),
      saldo_anterior: this.translate.instant('extrato_estoque.labels.saldo_anterior'),
      saldo_final: this.translate.instant('extrato_estoque.labels.saldo_final'),
      origem: this.translate.instant('extrato_estoque.labels.origem'),
      entrada: this.translate.instant('extrato_estoque.labels.entrada'),
      saida: this.translate.instant('extrato_estoque.labels.saida'),
      ajuste_de_estoque: this.translate.instant('extrato_estoque.labels.ajuste_de_estoque'),
      nota_fiscal: this.translate.instant('extrato_estoque.labels.nota_fiscal'),
      pedido: this.translate.instant('extrato_estoque.labels.pedido'),
      itens_por_pagina: this.translate.instant('extrato_estoque.labels.itens_por_pagina'),
      item: this.translate.instant('extrato_estoque.labels.item'),
      venda: this.translate.instant('extrato_estoque.labels.venda'),
      compra: this.translate.instant('extrato_estoque.labels.compra'),
      devolucao_de_venda: this.translate.instant('extrato_estoque.labels.devolucao_de_venda'),
      devolucao_de_compra: this.translate.instant('extrato_estoque.labels.devolucao_de_compra'),
    };

    this.placeholders = {
      pesquisar: this.translate.instant('extrato_estoque.placeholders.pesquisar'),
    };

    this.tooltips = {
      voltar: this.translate.instant('extrato_estoque.tooltips.voltar'),
      ver_ajuste: this.translate.instant('extrato_estoque.tooltips.ver_ajuste'),
      ver_nota: this.translate.instant('extrato_estoque.tooltips.ver_nota'),
    };

    this.botoes = {
      voltar: this.translate.instant('extrato_estoque.botoes.voltar'),
    };
  }

  // =========================================================================
  // Carregar dados
  // =========================================================================

  loadExtrato(page: number = 0, size: number = this.rows, sort: string = `${this.lastfield},${this.lastSort}`): void {
    this.loading = true;
    this.estoqueService.get('/estoque/extrato', {
      page,
      size,
      sort,
      itemId: this.itemId,
    }).subscribe({
      next: (response) => {
        this.extratos = response.content;
        this.totalRecords = response.totalElements;
        this.first = page * size;
        this.loading = false;

        // Extrair info do item do primeiro registro
        if (this.extratos.length > 0) {
          this.itemNome = this.extratos[0].item.nome;
          this.itemCodigo = this.extratos[0].item.codigoItem;
        }
      },
      error: () => {
        this.extratos = [];
        this.totalRecords = 0;
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.texts.erro_carregar,
        });
      },
    });
  }

  // =========================================================================
  // Busca
  // =========================================================================

  onSearchChange(value: string): void {
    this.searchTerm = value || '';
    // A busca no extrato nao filtra via backend (nao ha endpoint de filtro)
    // mas mantem o padrao visual. Se o backend suportar no futuro, basta
    // adicionar o parametro aqui.
  }

  // =========================================================================
  // Ordenacao backend (customSort)
  // =========================================================================

  customSort(event: SortEvent): void {
    let sort = 'desc';
    if (event.order === 1) {
      sort = 'asc';
    }

    if (this.lastfield === event.field && this.lastSort === sort) {
      return;
    }

    this.lastfield = String(event.field);
    this.lastSort = String(sort);
    const sortStr = `${this.lastfield},${this.lastSort}`;

    this.loadExtrato(0, this.rows, sortStr);
  }

  // =========================================================================
  // Paginacao
  // =========================================================================

  onPageChange(event: any): void {
    const page = event.page;
    const size = event.rows;
    this.rows = size;
    const sort = `${this.lastfield},${this.lastSort}`;
    this.loadExtrato(page, size, sort);
  }

  onRowsPerPageChange(): void {
    this.first = 0;
    const sort = `${this.lastfield},${this.lastSort}`;
    this.loadExtrato(0, this.rows, sort);
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        const storedAll = localStorage.getItem(this.STORAGE_KEY + '-all');
        const allKnownKeys = storedAll
          ? new Set(JSON.parse(storedAll) as string[])
          : new Set(visibleKeys);

        this.columns.forEach(col => {
          if (!col.fixed) {
            if (allKnownKeys.has(col.key)) {
              col.visible = visibleKeys.includes(col.key);
            } else {
              col.visible = col.defaultVisible;
            }
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }

  // =========================================================================
  // Origem da movimentacao
  // =========================================================================

  getOrigem(extrato: ExtratoEstoqueDTO): string {
    if (extrato.ajusteDeEstoque) {
      return this.labels.ajuste_de_estoque;
    }
    if (extrato.nota) {
      return this.getLabelTipoTransacao(extrato.nota.tipoDeTransacao);
    }
    return this.labels.pedido;
  }

  private getLabelTipoTransacao(tipo: any): string {
    // O backend pode retornar o enum como objeto { enum: string } ou como string direta
    const tipoStr: string = (tipo && typeof tipo === 'object' && 'enum' in tipo)
      ? String(tipo.enum).toUpperCase()
      : String(tipo ?? '').toUpperCase();

    switch (tipoStr) {
      case TipoTransacao.VENDA:
        return this.labels.venda;
      case TipoTransacao.COMPRA:
        return this.labels.compra;
      case TipoTransacao.DEVOLUCAO:
        return this.labels.devolucao_de_venda;
      case TipoTransacao.DEVOLUCAO_DE_COMPRA:
        return this.labels.devolucao_de_compra;
      default:
        return `${this.labels.nota_fiscal} ${tipoStr}`;
    }
  }

  // =========================================================================
  // Navegacao por linha
  // =========================================================================

  navegarParaDetalhe(extrato: ExtratoEstoqueDTO): void {
    if (extrato.ajusteDeEstoque) {
      this.router.navigate(['/ajuste-estoque', this.itemId]);
    } else if (extrato.nota) {
      this.router.navigate(['/notas-list/fatura', extrato.nota.id]);
    }
  }

  // =========================================================================
  // Navegacao
  // =========================================================================

  voltarParaLista(): void {
    this.router.navigate(['/produtos']);
  }
}
